import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Heart, Sparkles, ArrowRight } from 'lucide-react';

const CALENDLY_URL = 'https://calendly.com/shannoni/circleupsquadonboarding';

const WelcomePage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [scheduled, setScheduled] = useState(false);
  const widgetRef = useRef(null);

  // If a non-eligible role somehow lands here, bounce to dashboard.
  useEffect(() => {
    if (user && user.role !== 'patient' && user.role !== 'care_coordinator') navigate('/dashboard');
  }, [user, navigate]);

  // Load Calendly's embed script exactly once.
  useEffect(() => {
    const cssId = 'calendly-css';
    const scriptId = 'calendly-script';
    if (!document.getElementById(cssId)) {
      const link = document.createElement('link');
      link.id = cssId; link.rel = 'stylesheet';
      link.href = 'https://assets.calendly.com/assets/external/widget.css';
      document.head.appendChild(link);
    }
    if (!document.getElementById(scriptId)) {
      const s = document.createElement('script');
      s.id = scriptId; s.async = true;
      s.src = 'https://assets.calendly.com/assets/external/widget.js';
      document.body.appendChild(s);
    }
  }, []);

  // Listen for the "invitee scheduled" postMessage from Calendly so we can celebrate + advance.
  useEffect(() => {
    const handler = (e) => {
      if (typeof e.data !== 'object' || !e.data.event) return;
      if (e.data.event === 'calendly.event_scheduled') {
        setScheduled(true);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  const firstName = (user?.name || '').split(' ')[0] || 'friend';
  const prefillEmail = user?.email || '';
  const prefillName = user?.name || '';
  const widgetUrl = `${CALENDLY_URL}?hide_gdpr_banner=1&primary_color=0891b2&name=${encodeURIComponent(prefillName)}&email=${encodeURIComponent(prefillEmail)}`;

  return (
    <div className="min-h-screen bg-gradient-to-b from-cyan-50/40 via-background to-background dark:from-cyan-950/20 pb-20" data-testid="welcome-page">
      <div className="max-w-4xl mx-auto px-5 pt-10">
        {/* Hero */}
        <div className="text-center mb-8 fade-in">
          <div className="inline-flex items-center gap-2 bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide mb-4">
            <Sparkles className="w-3.5 h-3.5" /> Free Onboarding Session
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Welcome, {firstName}.
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground mt-3 max-w-2xl mx-auto">
            Before you dive in — book a <strong className="text-foreground">free 45-minute onboarding call</strong> with our founder,
            {' '}<strong className="text-foreground">Shannon Ivey</strong>. She{"'"}ll help you set up your circle, invite the right people, and make the most of every feature.
          </p>
        </div>

        {/* Scheduled state */}
        {scheduled ? (
          <div className="surface-card p-8 text-center max-w-xl mx-auto" data-testid="welcome-scheduled-card">
            <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <Heart className="w-8 h-8 text-emerald-600" fill="currentColor" />
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Booked! 🎉
            </h2>
            <p className="text-muted-foreground mb-6">
              You{"'"}ll get a calendar invite from Shannon in a moment. Talk to you soon!
            </p>
            <Button className="btn-primary" onClick={() => navigate('/dashboard')} data-testid="welcome-scheduled-continue-btn">
              Continue to my dashboard <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        ) : (
          <>
            {/* Calendly inline widget */}
            <div
              ref={widgetRef}
              className="calendly-inline-widget rounded-2xl overflow-hidden border border-border shadow-sm bg-background"
              data-url={widgetUrl}
              data-testid="calendly-widget"
              style={{ minWidth: '320px', height: '760px' }}
            />

            {/* Skip */}
            <div className="text-center mt-6">
              <button
                onClick={() => navigate('/dashboard')}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors underline underline-offset-4"
                data-testid="welcome-skip-btn"
              >
                Skip for now — I{"'"}ll book later
              </button>
              <p className="text-xs text-muted-foreground mt-2">You can always book at <a href={CALENDLY_URL} target="_blank" rel="noreferrer" className="text-cyan-600 hover:underline">calendly.com/shannoni</a></p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default WelcomePage;
